import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { usePendingGenerations } from '@/context/pending-generations.context';
import { openGenerationResult } from '@/lib/openGenerationResult';

const CUERPO_LABEL: Record<string, string> = {
  secretaria: 'Secretaría-Intervención',
  c1: 'Cuerpo General Estado',
  c2: 'Auxiliar Estado',
  iipp: 'IIPP',
  guardia_civil: 'Guardia Civil',
  policia_nacional: 'Policía Nacional',
};

const TYPE_LABEL: Record<string, string> = {
  test: 'test',
  case: 'supuesto práctico',
  flashcard: 'mazo de flashcards',
  presentation: 'presentación',
  infographic: 'infografía',
  report: 'resumen',
};

/**
 * Watches the pending generations list and fires a clickable sonner toast
 * whenever a generation flips to 'ready' AND the user is NOT currently on
 * its destination page. Mounted once inside <AppLayout/> and the cuerpo
 * layouts.
 */
export function GenerationReadyNotifier() {
  const { generations, markSeen, clear } = usePendingGenerations();
  const navigate = useNavigate();
  const location = useLocation();
  const announcedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    for (const gen of generations) {
      if (announcedRef.current.has(gen.id)) continue;

      if (gen.status === 'ready' && gen.resultId) {
        const target = gen.fullRoute || gen.route;
        const isExternal = /^(https?:|blob:|data:)/i.test(target);
        const onTarget = !isExternal && location.pathname.startsWith(gen.route);
        if (onTarget) {
          // The user is already on the page → the inline lateral popup
          // will handle it. Skip the global toast.
          continue;
        }
        announcedRef.current.add(gen.id);
        const cuerpoLabel = CUERPO_LABEL[gen.cuerpo] ?? gen.cuerpo;
        const typeLabel = TYPE_LABEL[gen.type] ?? 'material';
        toast.success(`Tu ${typeLabel} está listo`, {
          description: `${cuerpoLabel} · ${gen.topic}`,
          duration: 30_000,
          action: {
            label: 'Abrir',
            onClick: () => {
              markSeen(gen.id);
              if (!openGenerationResult(gen, navigate)) {
                toast.error('No hay vista previa disponible para este material.');
              }
            },
          },
        });
      } else if (gen.status === 'error') {
        announcedRef.current.add(gen.id);
        toast.error('No se pudo generar', {
          description: gen.errorMessage || 'Inténtalo de nuevo',
          action: { label: 'Cerrar', onClick: () => clear(gen.id) },
        });
      }
    }
  }, [generations, location.pathname, navigate, markSeen, clear]);

  return null;
}
